import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

const TOUR_STORAGE_KEY = "product_tour_completed";

export interface TourStep {
  targetId: string;
  title: string;
  description: string;
  placement?: "right" | "left" | "top" | "bottom";
}

export const TOUR_STEPS: TourStep[] = [
  {
    targetId: "tour-new-document",
    title: "Upload a document",
    description:
      "Click here to upload a new document. We'll extract and structure the data for you automatically.",
    placement: "right",
  },
  {
    targetId: "tour-dashboard",
    title: "Dashboard",
    description:
      "Get an overview of your activity, recent documents, and time saved.",
    placement: "right",
  },
  {
    targetId: "tour-all-documents",
    title: "All documents",
    description:
      "Browse and search through all your uploaded documents in one place.",
    placement: "right",
  },
  {
    targetId: "tour-new-label",
    title: "Organise with labels",
    description:
      "Create colour-coded labels to categorise and quickly filter your documents.",
    placement: "right",
  },
  {
    targetId: "tour-help",
    title: "Help centre",
    description:
      "Stuck? Click here to open the help centre or contact our support team.",
    placement: "top",
  },
];

interface TourContextType {
  isActive: boolean;
  currentStep: number;
  totalSteps: number;
  step: TourStep | null;
  next: () => void;
  skip: () => void;
  startTour: () => void;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

export const TourProvider = ({ children }: { children: ReactNode }) => {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    const completed = localStorage.getItem(TOUR_STORAGE_KEY);
    if (!completed) {
      // Small delay so the layout is fully rendered before we start
      const timer = setTimeout(() => setIsActive(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const endTour = () => {
    setIsActive(false);
    setCurrentStep(0);
    localStorage.setItem(TOUR_STORAGE_KEY, "true");
  };

  const next = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      endTour();
    }
  };

  const skip = () => endTour();

  const startTour = () => {
    localStorage.removeItem(TOUR_STORAGE_KEY);
    setCurrentStep(0);
    setIsActive(true);
  };

  return (
    <TourContext.Provider
      value={{
        isActive,
        currentStep,
        totalSteps: TOUR_STEPS.length,
        step: isActive ? (TOUR_STEPS[currentStep] ?? null) : null,
        next,
        skip,
        startTour,
      }}
    >
      {children}
    </TourContext.Provider>
  );
};

export const useTour = (): TourContextType => {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error("useTour must be used inside TourProvider");
  return ctx;
};
